import { Descriptions, Grid } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { type UserSpecsFragment } from '../../../api/fragments/UserSpecs';
import { useCompany } from '../../../components/contexts/CompanyContextManager';
import ConsolePageWithHeader from '../../../layouts/ConsoleLayout/ConsolePageWithHeader';
import useFormattedSimpleVersioning from '../../../utilities/useFormattedSimpleVersioning';
import UserActions from './UserActions';
import UserPermissionList from './UserPermissionList';

export type UserInnerPageProps = { user: UserSpecsFragment };

const UserInnerPage = ({ user }: UserInnerPageProps) => {
    const { t } = useTranslation(['userDetails']);
    const company = useCompany(true);
    // Use of company context as users can be tied to multiple companies
    const { updated, offset } = useFormattedSimpleVersioning({
        versioning: user.versioning,
        timeZone: company?.timeZone,
    });

    const navigate = useNavigate();
    const screens = Grid.useBreakpoint();

    const content = (
        <Descriptions column={{ xs: 1, md: 2 }} bordered>
            <Descriptions.Item label={t('userDetails:content.displayName')}>{user.displayName}</Descriptions.Item>
            <Descriptions.Item label={t('userDetails:content.email')}>{user.email}</Descriptions.Item>
            <Descriptions.Item label={t('userDetails:content.mobile')}>
                {`+${user.mobile.prefix} ${user.mobile.value}`}
            </Descriptions.Item>
            <Descriptions.Item label={t('userDetails:content.alias')}>{user.alias}</Descriptions.Item>
            <Descriptions.Item {...t('common:fields.updated', { returnObjects: true, offset })}>
                {updated}
            </Descriptions.Item>
        </Descriptions>
    );

    return (
        <ConsolePageWithHeader
            content={content}
            extra={<UserActions user={user} />}
            onBack={() => navigate('/admin/accesses/users')}
            title={screens.md ? t('userDetails:title', { name: user.displayName }) : user.displayName}
        >
            <UserPermissionList user={user} />
        </ConsolePageWithHeader>
    );
};

export default UserInnerPage;
